import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { calculateAge } from "@/utils/date";
import { Button, Divider, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { ReservationPreferencesSection } from "../ReservationPreferencesSection";
import { UserEventHistory } from "./UserEventHistory";
import { UserRatingsSection } from "./UserRatingsSection";

interface UserOverviewModalProps {
  request: ReservationRequestDto | null;
  onClose: () => void;
}

const GENDER_LABELS: Record<string, string> = {
  MALE: "남성",
  FEMALE: "여성",
  NON_BINARY: "논바이너리",
};

const GROUP_GENDER_LABELS: Record<string, string> = {
  ALL_GENDERS_OKAY: "모든 성별 괜찮아요",
  SAME_GENDER_PREFERRED: "동성 선호",
};

const GROUP_AGE_LABELS: Record<string, string> = {
  ALL_AGES_OKAY: "모든 연령 괜찮아요",
  SAME_AGE_PREFERRED: "비슷한 연령 선호",
};

export const UserOverviewModal = ({ request, onClose }: UserOverviewModalProps) => {
  const [showPersonalityTest, setShowPersonalityTest] = useState(false);

  if (!request) return null;

  const { user } = request;
  const age = calculateAge(user.birthDate);

  return (
    <Modal
      opened={!!request}
      onClose={() => {
        setShowPersonalityTest(false);
        onClose();
      }}
      title={`${user.name} (${user.nickname})`}
      size="lg"
      styles={{ title: { color: "black", fontWeight: 700 } }}
    >
      <Stack gap="md">
        <div>
          <Title order={5} c="black" mb="xs">
            기본 정보
          </Title>
          <Group>
            <Text c="black" size="sm">
              <Text span fw={700}>
                나이:
              </Text>{" "}
              {age}세
            </Text>
            <Text c="black" size="sm">
              <Text span fw={700}>
                성별:
              </Text>{" "}
              {GENDER_LABELS[user.gender] ?? user.gender}
            </Text>
            <Text c="black" size="sm">
              <Text span fw={700}>
                국적:
              </Text>{" "}
              {user.nationality}
            </Text>
            <Text c="black" size="sm">
              <Text span fw={700}>
                직업:
              </Text>{" "}
              {user.profession}
            </Text>
          </Group>
          <Group mt="xs">
            <Text c="black" size="sm">
              <Text span fw={700}>
                그룹 성별 선호:
              </Text>{" "}
              {user.groupGenderPreference
                ? (GROUP_GENDER_LABELS[user.groupGenderPreference] ?? "미설정")
                : "미설정"}
            </Text>
            <Text c="black" size="sm">
              <Text span fw={700}>
                그룹 연령 선호:
              </Text>{" "}
              {user.groupAgePreference
                ? (GROUP_AGE_LABELS[user.groupAgePreference] ?? "미설정")
                : "미설정"}
            </Text>
          </Group>
        </div>

        <Divider />

        <div>
          <Title order={5} c="black" mb="xs">
            예약 Preferences
          </Title>
          <ReservationPreferencesSection
            preferredLanguage={request.preferredLanguage}
            preferredCost={request.preferredCost}
            dietaryRestrictionSlugs={request.dietaryRestrictionSlugs}
            dietaryRestrictionsOtherText={request.dietaryRestrictionsOtherText}
            messageForGuests={request.messageForGuests}
          />
        </div>

        <Divider />

        <div>
          <Group mb="xs">
            <Title order={5} c="black">
              Personality Test
            </Title>
            <Button
              variant="outline"
              size="xs"
              onClick={() => setShowPersonalityTest(!showPersonalityTest)}
            >
              {showPersonalityTest ? "숨기기" : "보기"}
            </Button>
          </Group>
          {showPersonalityTest && (
            <Stack gap="xs">
              {user.personalityTestAnswers && user.personalityTestAnswers.length > 0 ? (
                user.personalityTestAnswers.map((answer) => (
                  <Text c="black" size="sm" key={answer.id}>
                    <Text span fw={700} c="black">
                      {answer.question.title}:
                    </Text>{" "}
                    {answer.answerText ||
                      answer.answerScale ||
                      answer.selectedOptions.map((o) => o.option.displayText).join(", ")}
                  </Text>
                ))
              ) : (
                <Text c="dimmed" size="sm">
                  성격 테스트 답변이 없습니다
                </Text>
              )}
            </Stack>
          )}
        </div>

        <Divider />

        <div>
          <Title order={5} c="black" mb="xs">
            이벤트 참석 이력
          </Title>
          <UserEventHistory userId={user.uid} />
        </div>

        <Divider />

        <div>
          <Title order={5} c="black" mb="xs">
            유저 평가
          </Title>
          <UserRatingsSection userId={user.uid} />
        </div>
      </Stack>
    </Modal>
  );
};
